<template>
  <div class="p-4 min-h-screen bg-gray-100">
    <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-4 max-w-6xl mx-auto">
      <div class="flex items-center justify-between mb-3">
        <h1 class="text-xl font-semibold">
          Outreach Responses
          <span class="ml-2 align-middle text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 uppercase tracking-wide">dev</span>
        </h1>
        <span v-if="!loading" class="text-sm text-gray-500">
          {{ count }} message{{ count === 1 ? '' : 's' }}
        </span>
      </div>

      <p class="text-xs text-gray-500 mb-4">
        Everything the response poll has recorded, across all analysts' outreach: inbound
        replies, replies composed in Outlook rather than through the app, and delivery
        failures. Filter by Kind to read this as "who responded" — a bounce or one of our
        own messages counted as a reply would look like interest. Each row is attached to
        the thread's original send, so opening one shows the whole thread.
      </p>

      <!-- Filters -->
      <div class="border border-gray-200 rounded-md bg-gray-50 p-3 mb-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-xs font-semibold uppercase tracking-wide text-gray-500">Filters</span>
          <div class="flex items-center gap-3">
            <button
              v-if="hasActiveFilters"
              type="button"
              @click="clearFilters"
              class="text-xs text-indigo-600 hover:text-indigo-800"
            >Clear all</button>
            <button
              type="button"
              @click="refresh"
              :disabled="loading"
              class="text-xs px-2 py-1 border border-gray-300 rounded-md bg-white hover:bg-gray-100 disabled:opacity-40"
            >{{ loading ? 'Refreshing…' : 'Refresh' }}</button>
          </div>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Client</label>
            <div class="filter-typeahead w-full">
              <TypeAheadMultiSelectBox
                :options="filters.client.options"
                :default-value="filters.client.value"
                select-multiple
                placeholder="Search clients…"
                no-selection-message="All clients"
                @update-value="onFilterChange('client', $event)"
              />
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Campaign</label>
            <div class="filter-typeahead w-full">
              <TypeAheadMultiSelectBox
                :options="filters.campaign.options"
                :default-value="filters.campaign.value"
                select-multiple
                placeholder="Search campaigns…"
                no-selection-message="All active campaigns"
                @update-value="onFilterChange('campaign', $event)"
              />
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Email Template</label>
            <div class="filter-typeahead w-full">
              <TypeAheadMultiSelectBox
                :options="filters.emailTemplate.options"
                :default-value="filters.emailTemplate.value"
                select-multiple
                placeholder="Search templates…"
                no-selection-message="All templates"
                @update-value="onFilterChange('emailTemplate', $event)"
              />
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Received</label>
            <div class="flex items-center gap-1">
              <input
                type="date"
                v-model="filters.receivedAfter.value"
                @change="onDateFilterChange"
                title="Received on or after"
                class="border rounded-md p-1 text-xs h-8 bg-white w-full"
              />
              <span class="text-gray-400 text-xs shrink-0">–</span>
              <input
                type="date"
                v-model="filters.receivedBefore.value"
                @change="onDateFilterChange"
                title="Received on or before"
                class="border rounded-md p-1 text-xs h-8 bg-white w-full"
              />
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Kind</label>
            <!-- Plain multi-select dropdown, not the typeahead the other filters
                 use: there are three fixed choices, so there is nothing to search.
                 Border and rounding come from the scoped rules below rather than
                 this wrapper -- see the note there. -->
            <div class="kind-filter relative h-8">
              <ListBox
                :options="kindFilterOptions"
                :model-value="filters.kind.value"
                multiple
                placeholder="All kinds"
                @update:modelValue="onFilterChange('kind', $event)"
              />
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Search</label>
            <input
              type="text"
              v-model="filters.search.value"
              @input="onSearchInput"
              placeholder="Sender, subject, or company…"
              class="border rounded-md p-1 px-2 text-xs h-8 bg-white w-full"
            />
          </div>
        </div>
      </div>

      <div v-if="loading" class="text-gray-500 py-8 text-center">Loading…</div>

      <div v-else-if="responses.length === 0" class="text-gray-500 py-8 text-center">
        {{ hasActiveFilters ? 'Nothing matches these filters.' : 'Nothing recorded yet.' }}
      </div>

      <!-- table-fixed + explicit column widths: the text columns hold their share
           of the row and truncate, instead of stretching the table past the card
           (which pushed the Thread link off the edge). Narrow viewports scroll. -->
      <div v-else class="overflow-x-auto">
      <table class="w-full min-w-[56rem] table-fixed border-collapse text-sm">
        <thead>
          <tr class="text-left border-b border-gray-300 text-gray-600">
            <th class="py-2 pr-2 w-8"></th>
            <th class="py-2 pr-3 w-[12%]">Received</th>
            <th class="py-2 pr-3 w-[10%]">Kind</th>
            <th class="py-2 pr-3 w-[16%]">From</th>
            <th class="py-2 pr-3 w-[14%]">Company</th>
            <th class="py-2 pr-3 w-[13%]">Campaign</th>
            <th class="py-2 pr-3 w-[15%]">Subject</th>
            <th class="py-2 pr-3 w-[10%]">Sent by</th>
            <th class="py-2 w-20"></th>
          </tr>
        </thead>
        <tbody>
          <!-- Each reply is a summary row; expanding it reveals the reply body
               in place, so scanning the feed doesn't need a page load per row. -->
          <template v-for="r in responses" :key="r.id">
            <tr
              class="border-b border-gray-100 hover:bg-gray-50 cursor-pointer align-top"
              @click="toggleExpanded(r.id)"
            >
              <td class="py-2 pr-2 text-gray-400">
                <ChevronDownIcon v-if="isExpanded(r.id)" class="w-4 h-4" />
                <ChevronRightIcon v-else class="w-4 h-4" />
              </td>
              <td class="py-2 pr-3 truncate" :title="formatDate(r.received_at)">{{ formatDate(r.received_at) }}</td>
              <td class="py-2 pr-3">
                <span
                  class="text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wide whitespace-nowrap"
                  :class="kindMeta(r.kind).badgeClass"
                >{{ kindMeta(r.kind).label }}</span>
              </td>
              <td class="py-2 pr-3" :title="r.from_email || ''">
                <div class="truncate">{{ r.from_name || r.from_email || 'Unknown sender' }}</div>
                <div v-if="r.from_name && r.from_email" class="text-xs text-gray-400 truncate">{{ r.from_email }}</div>
              </td>
              <td class="py-2 pr-3 truncate" :title="r.company ? r.company.company_name : ''">{{ r.company ? r.company.company_name : '—' }}</td>
              <td class="py-2 pr-3 truncate" :title="r.campaign ? r.campaign.campaign_name : ''">{{ r.campaign ? r.campaign.campaign_name : '—' }}</td>
              <td class="py-2 pr-3 truncate" :title="r.subject || ''">{{ r.subject || '—' }}</td>
              <td class="py-2 pr-3 truncate" :title="r.sent_by ? r.sent_by.name : ''">{{ r.sent_by ? r.sent_by.name : '—' }}</td>
              <td class="py-2 text-right" @click.stop>
                <button
                  type="button"
                  @click="openThread(r)"
                  class="text-xs text-indigo-600 hover:text-indigo-800 whitespace-nowrap"
                >Thread →</button>
              </td>
            </tr>
            <tr v-if="isExpanded(r.id)" class="border-b border-gray-100 bg-gray-50">
              <td></td>
              <td colspan="8" class="py-3 pr-3">
                <div class="text-xs text-gray-500 mb-2">
                  On <span class="text-gray-700">{{ r.draft_subject || '(no subject)' }}</span>
                  <template v-if="r.draft_sent_at"> · sent {{ formatDate(r.draft_sent_at) }}</template>
                  <template v-if="r.draft_recipient_email"> to {{ r.draft_recipient_email }}</template>
                  <template v-if="r.email_template"> · {{ r.email_template.email_template_name }}</template>
                </div>
                <!-- Sanitized server-side at ingest (nh3: scripts stripped, remote
                     images dropped); the reply's new content only, quoted history removed. -->
                <div v-if="r.body_html" class="text-sm text-gray-700 email-body bg-white border border-gray-200 rounded-md p-3" v-html="r.body_html"></div>
                <div v-else class="text-sm text-gray-400 italic">No body captured.</div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      </div>

      <div v-if="!loading && (prev || next)" class="flex items-center gap-2 mt-4 justify-end">
        <button :disabled="!prev" @click="loadResponses(page - 1)" class="px-3 py-1 border rounded-md text-sm disabled:opacity-40">Prev</button>
        <span class="text-sm text-gray-600">Page {{ page }} of {{ numPages || 1 }}</span>
        <button :disabled="!next" @click="loadResponses(page + 1)" class="px-3 py-1 border rounded-md text-sm disabled:opacity-40">Next</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import { getOutreachResponses } from '@/services/OutreachResponse'
import { getMinimalActiveCampaigns } from '@/services/Campaign'
import { getClients } from '@/services/Client'
import { getEmailTemplates } from '@/services/EmailTemplates'
import TypeAheadMultiSelectBox from '@/components/base-components/TypeAheadMultiSelectBox.vue'
import ListBox from '@/components/base-components/ListBox.vue'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/vue/20/solid'
import { kindMeta, KIND_FILTER_OPTIONS } from '@/utils/outreachResponseKind'

const store = useStore()
const route = useRoute()
const router = useRouter()
const $toast = useToast()

// The filters and page live in the URL (under the same keys the API uses), so
// opening a thread and coming back restores the exact view -- and so a filtered
// feed can be reloaded or linked to.
function queryIds(key) {
  const v = route.query[key]
  if (v === undefined || v === null || v === '') return []
  return (Array.isArray(v) ? v : [v]).map(Number).filter(n => !isNaN(n))
}

function queryStrings(key) {
  const v = route.query[key]
  if (v === undefined || v === null || v === '') return []
  return (Array.isArray(v) ? v : [v]).filter(x => typeof x === 'string' && x)
}

function queryText(key) {
  const v = route.query[key]
  return typeof v === 'string' ? v : ''
}

const responses = ref([])
const loading = ref(false)
const count = ref(0)
const page = ref(Number(route.query.page) || 1)
const next = ref(null)
const prev = ref(null)
const numPages = ref(1)

// Rows whose body is open. Ids only, so paging doesn't have to reconcile objects.
const expandedIds = ref([])

// Feed filters. `param` is the query-string key the OutreachResponseFilter
// backend reads (and the key this page keeps in its own URL); multi-select
// `value`s are arrays of ids.
const filters = reactive({
  client: { value: queryIds('client'), options: [], param: 'client' },
  campaign: { value: queryIds('campaign'), options: [], param: 'campaign' },
  emailTemplate: { value: queryIds('email_template'), options: [], param: 'email_template' },
  receivedAfter: { value: queryText('received_after'), param: 'received_after' },
  receivedBefore: { value: queryText('received_before'), param: 'received_before' },
  search: { value: queryText('search'), param: 'search' },
  kind: { value: queryStrings('kind'), param: 'kind' },
})

// Fixed server-side choices rather than a fetched list, unlike the other
// multi-selects -- these are OutreachResponse.Kind values, not ids.
const kindFilterOptions = KIND_FILTER_OPTIONS

// Mirror the current filters + page into the URL. Called after each load, so the
// address bar always describes what's on screen.
function syncQuery() {
  const q = {}
  if (filters.client.value.length) q.client = filters.client.value.map(String)
  if (filters.campaign.value.length) q.campaign = filters.campaign.value.map(String)
  if (filters.emailTemplate.value.length) q.email_template = filters.emailTemplate.value.map(String)
  if (filters.receivedAfter.value) q.received_after = filters.receivedAfter.value
  if (filters.receivedBefore.value) q.received_before = filters.receivedBefore.value
  if (filters.search.value) q.search = filters.search.value
  if (filters.kind.value.length) q.kind = [...filters.kind.value]
  if (page.value > 1) q.page = String(page.value)
  router.replace({ query: q })
}

const hasActiveFilters = computed(() =>
  filters.client.value.length > 0 ||
  filters.campaign.value.length > 0 ||
  filters.emailTemplate.value.length > 0 ||
  !!filters.receivedAfter.value ||
  !!filters.receivedBefore.value ||
  !!filters.search.value ||
  filters.kind.value.length > 0
)

function isExpanded(id) {
  return expandedIds.value.includes(id)
}

function toggleExpanded(id) {
  const i = expandedIds.value.indexOf(id)
  if (i === -1) expandedIds.value.push(id)
  else expandedIds.value.splice(i, 1)
}

// The reply's draft is the thread's original send, so its preview shows the
// outreach plus every reply on the thread. `from` carries this view (filters,
// page and all) so the preview's Back button returns here rather than dropping
// the analyst into the drafts queue.
function openThread(response) {
  if (!response.outreach_draft) return
  router.push({
    path: `/outreach-drafts/${response.outreach_draft}/preview`,
    query: { from: route.fullPath },
  })
}

async function loadResponses(toPage = 1) {
  loading.value = true
  try {
    const token = store.state.auth.token
    const params = { page: toPage }
    // Arrays are serialized as repeated params by the service's paramsSerializer.
    if (filters.client.value.length) params.client = filters.client.value
    if (filters.campaign.value.length) params.campaign = filters.campaign.value
    if (filters.emailTemplate.value.length) params.email_template = filters.emailTemplate.value
    if (filters.receivedAfter.value) params.received_after = filters.receivedAfter.value
    if (filters.receivedBefore.value) params.received_before = filters.receivedBefore.value
    if (filters.search.value) params.search = filters.search.value
    if (filters.kind.value.length) params.kind = filters.kind.value
    const res = await getOutreachResponses(token, params)
    responses.value = res.results || []
    count.value = res.count || 0
    next.value = res.next
    prev.value = res.prev
    page.value = res.page || toPage
    numPages.value = res.numPages || 1
    syncQuery()
  } catch (e) {
    $toast.error(e?.response?.data?.detail || 'Failed to load responses')
  } finally {
    loading.value = false
  }
}

// Values are ids for the model-backed filters and kind strings for Kind; the
// multi-select stores whatever `option.value` holds, so both work unchanged.
function onFilterChange(key, selectedValues) {
  filters[key].value = selectedValues
  loadResponses(1)
}

function onDateFilterChange() {
  loadResponses(1)
}

// Typing shouldn't fire a request per keystroke; reload once the analyst pauses.
let searchTimer = null
function onSearchInput() {
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(() => { searchTimer = null; loadResponses(1) }, 400)
}

onBeforeUnmount(() => {
  if (searchTimer) clearTimeout(searchTimer)
})

function clearFilters() {
  filters.client.value = []
  filters.campaign.value = []
  filters.emailTemplate.value = []
  filters.receivedAfter.value = ''
  filters.receivedBefore.value = ''
  filters.search.value = ''
  filters.kind.value = []
  loadResponses(1)
}

function refresh() {
  loadResponses(page.value)
}

async function loadFilterOptions() {
  const token = store.state.auth.token
  try {
    const campaigns = await getMinimalActiveCampaigns(token)
    filters.campaign.options = (campaigns || []).map(c => ({ label: c.campaign_name, value: c.id }))
  } catch (e) {
    $toast.error('Failed to load campaigns')
  }
  try {
    const res = await getClients({ token, endpoint: '?get_all=true' })
    filters.client.options = (res.data || []).map(c => ({ label: c.client_name, value: c.id }))
  } catch (e) {
    $toast.error('Failed to load clients')
  }
  try {
    const templates = await getEmailTemplates(token)
    filters.emailTemplate.options = (templates || []).map(t => ({ label: t.email_template_name, value: t.id }))
  } catch (e) {
    $toast.error('Failed to load email templates')
  }
}

function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  const p = (n) => String(n).padStart(2, '0')
  let h = d.getHours()
  const ampm = h >= 12 ? 'PM' : 'AM'
  h = h % 12 || 12
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${String(d.getFullYear()).slice(-2)} ${h}:${p(d.getMinutes())} ${ampm}`
}

onMounted(() => {
  loadFilterOptions()
  // Start from whatever the URL describes -- a returning analyst, a reload, or a
  // shared link all land on the same view.
  loadResponses(page.value)
})
</script>

<style scoped>
/* Shrink the selected pills so they fit within the input row (the shared
   component's default pill overflows the ~32px tall box and gets clipped).
   Same treatment the Outreach Drafts filters use. */
.filter-typeahead :deep(.bg-indigo-200) {
  font-size: 0.7rem;
  line-height: 1rem;
  padding-top: 1px;
  padding-bottom: 1px;
  margin-top: 2px;
  margin-bottom: 2px;
}

/* Reply HTML comes from an external mail client, so give it email-ish defaults
   rather than letting it inherit the app's resets. */
.email-body :deep(img) {
  max-width: 100%;
  height: auto;
}
.email-body :deep(a) {
  color: #2563eb;
  text-decoration: underline;
}
.email-body :deep(p) {
  margin: 0 0 0.5rem;
}
/* ListBox is built for the FilterSet layout: it paints its own square white
   backgrounds and rounds only the bottom of its button, so dropping it into a
   rounded, bordered box leaves the corners looking clipped. Style its elements
   directly instead of wrapping them -- overflow-hidden on the wrapper squares it
   off correctly but also clips the absolutely-positioned options panel, which
   makes the dropdown vanish (it stays keyboard-selectable, which is what makes
   that breakage look like a missing menu rather than a style bug). */
.kind-filter :deep(.bg-white) {
  border-radius: 0.375rem; /* rounded-md, matching the inputs beside it */
}
.kind-filter :deep(button) {
  border: 1px solid rgb(209 213 219); /* gray-300, matching the inputs */
}

/* Bounce bodies are the mail system's report, escaped into a <pre> at ingest.
   Wrap it, or a long diagnostic line stretches the table off the page. */
.email-body :deep(pre) {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.75rem;
  line-height: 1.15rem;
  margin: 0;
}
</style>
